import dayjs from 'dayjs';
import { fromPairs, isNil, map, pipe } from 'ramda';

import { PremiumPay, PremiumPayFlagsEnum } from '../../../types';
import { FlagField, PremiumPayFormValues, PremiumPayForSave } from '../types';
import { CLAOption } from 'types';

import { isSvcTypeBonus } from '../../../utils/utils';
import { includes } from 'lodash';
import { normalizeTimeRange } from 'utils/date';

const mapFormFlagsForApi = (formFlagsData: FlagField[]) => {
  const flagsArr: PremiumPayFlagsEnum[] = [];
  for (const flag in formFlagsData) {
    if (formFlagsData[flag]) {
      flagsArr.push(PremiumPayFlagsEnum[flag]);
    }
  }
  return flagsArr;
};

export const convertResponseToForm = (
  data: PremiumPay
): PremiumPayFormValues => {
  return {
    ...data,
    bonusTypeSvcName: data.bonusTypeSvc ?? '',
    bonusTypeSvc: {
      label: data.bonusTypeSvc,
      value: data.bonusTypeSvc
    },
    valid: {
      from: new Date(data.valid.from),
      to: new Date(data.valid.to)
    },
    timeFrom: dayjs(data.timeFrom),
    timeTo: dayjs(data.timeTo),
    timeFromThr: !isNil(data?.timeFromThr)
      ? dayjs(data.timeFromThr)
      : undefined,
    timeToThr: !isNil(data?.timeToThr) ? dayjs(data.timeToThr) : undefined,
    flags: pipe(
      map<string, [string, boolean]>((value) => [value, true]),
      fromPairs
    )(data.flags) as unknown as FlagField[],
    scPri: data.scPri ?? '',
    scSec: data.scSec ?? ''
  };
};

export const convertPayloadForSave = (
  data: PremiumPayFormValues,
  premiumPayTypes: CLAOption[] | undefined
): PremiumPayForSave => {
  const flags = mapFormFlagsForApi(data.flags);
  const notIncludedDayFlags = Object.values(PremiumPayFlagsEnum).filter(
    (day) => !includes(flags, day)
  );
  const rootFlags: Record<string, boolean> = {
    ...Object.fromEntries(flags.map((flag) => [flag, true])),
    ...Object.fromEntries(notIncludedDayFlags.map((flag) => [flag, false]))
  };

  const range = normalizeTimeRange(data.timeFrom, data.timeTo);

  return {
    percentage: data.percentage,
    columnNr: data.columnNr,
    bonusTypeSvc: data?.bonusTypeSvc?.value,
    valid: {
      from: data.valid.from?.toISOString(),
      to: data.valid.to?.toISOString()
    },
    flags,
    timeFrom: range.timeFrom.toISOString(),
    timeTo: range.timeTo.toISOString(),
    claId:
      premiumPayTypes &&
      premiumPayTypes.filter((item) => item.code == data.claCode)[0].claId,
    ...(data?.claBonusId && {
      claBonusId: data.claBonusId
    }),
    ...(flags.includes(PremiumPayFlagsEnum.TIME_FROM_THR) && data?.timeFromThr
      ? { timeFromThr: data.timeFromThr.toISOString() }
      : { timeFromThr: null }),
    // The thresholds keep their original comparison and are deliberately not run
    // through normalizeTimeRange: they are not a range. BuhCalculationRules tests
    // timeFromThr and timeToThr independently, so turning equal thresholds into a
    // 24h window moves one of them a full day and switches the bonus off. See the
    // "Afgewogen en niet gedaan" section of #314.
    ...(flags.includes(PremiumPayFlagsEnum.TIME_TO_THR) && data?.timeToThr
      ? {
          timeToThr:
            data?.timeFromThr && data?.timeToThr < data?.timeFromThr
              ? dayjs(data.timeToThr).add(1, 'day').toISOString()
              : data.timeToThr.toISOString()
        }
      : { timeToThr: null }),
    ...(data?.maxHoursPerDayrange && {
      maxHoursPerDayrange: data.maxHoursPerDayrange
    }),
    bonus25Method:
      isSvcTypeBonus(data.bonusTypeSvc?.value) && data?.bonus25Method
        ? data.bonus25Method
        : null,
    scPri: data.scPri,
    scSec: data.scSec,
    maxHoursPerDayrange:
      isSvcTypeBonus(data.bonusTypeSvc?.value) && data?.maxHoursPerDayrange
        ? data.maxHoursPerDayrange
        : 0,
    dayrange:
      isSvcTypeBonus(data.bonusTypeSvc?.value) && data?.dayrange
        ? data.dayrange
        : null,
    ...rootFlags
  };
};
